import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { HardHat, LogOut, LayoutDashboard, BookOpenText } from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  function doLogout() {
    logout();
    nav("/login", { replace: true });
  }

  return (
    <div className="min-h-screen min-h-[100dvh] bg-background flex flex-col">
      <header className="sticky top-0 z-20 bg-navy text-white border-b border-navy-hover">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-2">
          <Link to="/projects" data-testid="brand-home" className="flex items-center gap-2.5 min-w-0">
            <div className="h-9 w-9 rounded-md bg-brandblue flex items-center justify-center shadow shrink-0">
              <HardHat className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-display font-bold text-lg tracking-tight">BuildTrack</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-300 hidden sm:block">Cost control for builders</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <NavItem to="/projects" testId="nav-projects" icon={<LayoutDashboard className="h-4 w-4" />}>Projects</NavItem>
            <NavItem to="/ledger"   testId="nav-ledger"   icon={<BookOpenText className="h-4 w-4" />}>Ledger</NavItem>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden sm:block text-right leading-tight max-w-[160px]">
              <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={doLogout}
              data-testid="btn-logout"
              className="h-10 border-white/20 text-white bg-white/5 hover:bg-white/10 hover:text-white"
            >
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
        {/* Mobile nav */}
        <div className="md:hidden border-t border-navy-hover flex">
          <MobileNavItem to="/projects" testId="nav-projects-mobile" icon={<LayoutDashboard className="h-4 w-4" />}>Projects</MobileNavItem>
          <MobileNavItem to="/ledger"   testId="nav-ledger-mobile"   icon={<BookOpenText className="h-4 w-4" />}>Ledger</MobileNavItem>
        </div>
      </header>
      <main className="max-w-7xl mx-auto w-full p-4 md:p-8 flex-1">{children}</main>
    </div>
  );
}

function NavItem({ to, testId, icon, children }) {
  return (
    <NavLink to={to} data-testid={testId} className={({ isActive }) =>
      `px-3 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-2 ${
        isActive ? "bg-white/10 text-white" : "text-slate-300 hover:text-white hover:bg-white/5"
      }`
    }>
      {icon} {children}
    </NavLink>
  );
}

function MobileNavItem({ to, testId, icon, children }) {
  return (
    <NavLink to={to} data-testid={testId} className={({ isActive }) =>
      `flex-1 h-11 flex items-center justify-center gap-1.5 text-sm font-medium transition-colors ${
        isActive ? "bg-white/10 text-white" : "text-slate-300 hover:text-white"
      }`
    }>
      {icon} {children}
    </NavLink>
  );
}
